import { Injectable } from '@angular/core';
import { Condition, ConditionMode } from '../../shared/models/structure';
import {
  MIN_ALARM_DELAY,
  MAX_ALARM_DELAY,
  MIN_DECIMAL_PLACES,
  MAX_DECIMAL_PLACES,
  MULTIPLIER_VALUES,
  CONVERSION_VALUES,
  CompareOperator,
  AUTO_CLEAR_VALUES,
} from '../../shared/constants/tile-config.constants';

export interface ValidationResult {
  valid: boolean;
  errors: string[];
}

/**
 * Shared validation logic for tiles
 * Used by both tile editor UI and CSV import validation
 */
@Injectable({
  providedIn: 'root'
})
export class TileValidationService {
  /**
   * Validate min/max writable values
   */
  validateMinMax(min: number | null, max: number | null): ValidationResult {
    const errors: string[] = [];

    if (min != null && max != null && !isNaN(min) && !isNaN(max)) {
      if (min >= max) {
        errors.push('Minimum value must be less than maximum value');
      }
    }

    return { valid: errors.length === 0, errors };
  }

  /**
   * Validate alarm delay range
   */
  validateAlarmDelay(delay: number): ValidationResult {
    const errors: string[] = [];

    if (delay < MIN_ALARM_DELAY || delay > MAX_ALARM_DELAY) {
      errors.push(`Delay must be between ${MIN_ALARM_DELAY} and ${MAX_ALARM_DELAY} seconds`);
    }

    return { valid: errors.length === 0, errors };
  }

  /**
   * Validate alarm operator matches direction (high vs low)
   */
  validateAlarmOperator(alarmLevel: string, operator: string): ValidationResult {
    const errors: string[] = [];
    const isHighAlarm = alarmLevel.toLowerCase().includes('high');
    const isLowAlarm = alarmLevel.toLowerCase().includes('low');

    if (isHighAlarm && (operator === CompareOperator.LessThan || operator === CompareOperator.LessThanOrEqual)) {
      errors.push(`High alarms should use '>' or '>=' operators, got "${operator}"`);
    } else if (isLowAlarm && (operator === CompareOperator.GreaterThan || operator === CompareOperator.GreaterThanOrEqual)) {
      errors.push(`Low alarms should use '<' or '<=' operators, got "${operator}"`);
    }

    return { valid: errors.length === 0, errors };
  }

  /**
   * Validate alarm threshold values for numeric tiles
   * Ensures: High Critical > High Warning > Low Warning > Low Critical
   */
  validateThresholdValues(conditions: Condition[]): ValidationResult {
    const errors: string[] = [];
    const enabledConditions = conditions.filter(c => c.Enabled);

    if (enabledConditions.length < 2) {
      return { valid: true, errors: [] };
    }

    // Group by mode and direction
    const highCritical = enabledConditions.find(c =>
      c.Mode === ConditionMode.Critical &&
      (c.Comparison.Operator === CompareOperator.GreaterThan || c.Comparison.Operator === CompareOperator.GreaterThanOrEqual)
    );
    const highWarning = enabledConditions.find(c =>
      c.Mode === ConditionMode.Warning &&
      (c.Comparison.Operator === CompareOperator.GreaterThan || c.Comparison.Operator === CompareOperator.GreaterThanOrEqual)
    );
    const lowWarning = enabledConditions.find(c =>
      c.Mode === ConditionMode.Warning &&
      (c.Comparison.Operator === CompareOperator.LessThan || c.Comparison.Operator === CompareOperator.LessThanOrEqual)
    );
    const lowCritical = enabledConditions.find(c =>
      c.Mode === ConditionMode.Critical &&
      (c.Comparison.Operator === CompareOperator.LessThan || c.Comparison.Operator === CompareOperator.LessThanOrEqual)
    );

    // Validate ordering
    if (highCritical && highWarning && highCritical.Comparison.Value <= highWarning.Comparison.Value) {
      errors.push(`High Critical threshold (${highCritical.Comparison.Value}) must be greater than High Warning threshold (${highWarning.Comparison.Value})`);
    }

    if (lowWarning && lowCritical && lowWarning.Comparison.Value <= lowCritical.Comparison.Value) {
      errors.push(`Low Warning threshold (${lowWarning.Comparison.Value}) must be greater than Low Critical threshold (${lowCritical.Comparison.Value})`);
    }

    if (highWarning && lowWarning && highWarning.Comparison.Value <= lowWarning.Comparison.Value) {
      errors.push(`High Warning threshold (${highWarning.Comparison.Value}) must be greater than Low Warning threshold (${lowWarning.Comparison.Value})`);
    }

    if (highCritical && lowCritical && highCritical.Comparison.Value <= lowCritical.Comparison.Value) {
      errors.push(`High Critical threshold (${highCritical.Comparison.Value}) must be greater than Low Critical threshold (${lowCritical.Comparison.Value})`);
    }

    return { valid: errors.length === 0, errors };
  }

  /**
   * Validate offset is a valid number
   */
  validateOffset(offset: number | null): ValidationResult {
    const errors: string[] = [];

    if (offset != null && isNaN(offset)) {
      errors.push('Offset must be a valid number');
    }

    return { valid: errors.length === 0, errors };
  }

  /**
   * Validate decimal places range
   */
  validateDecimalPlaces(decimal: number): ValidationResult {
    const errors: string[] = [];

    if (isNaN(decimal) || decimal < MIN_DECIMAL_PLACES || decimal > MAX_DECIMAL_PLACES) {
      errors.push(`Decimal Places must be between ${MIN_DECIMAL_PLACES} and ${MAX_DECIMAL_PLACES}`);
    }

    return { valid: errors.length === 0, errors };
  }

  /**
   * Validate multiplier is a valid option
   */
  validateMultiplier(multiplier: string): ValidationResult {
    const errors: string[] = [];

    if (!MULTIPLIER_VALUES.includes(multiplier as any)) {
      errors.push(`Invalid Multiplier: "${multiplier}". Must be one of: ${MULTIPLIER_VALUES.join(', ')}`);
    }

    return { valid: errors.length === 0, errors };
  }

  /**
   * Validate conversion formula is a valid option
   */
  validateConversionFormula(conversion: string): ValidationResult {
    const errors: string[] = [];

    if (!CONVERSION_VALUES.includes(conversion as any)) {
      errors.push(`Invalid Conversion Formula: "${conversion}". Must be one of: ${CONVERSION_VALUES.join(', ')}`);
    }

    return { valid: errors.length === 0, errors };
  }

  validateAutoClear(value: string): ValidationResult {
    const errors: string[] = [];

    const normalized = value.toLowerCase();

    if (normalized !== 'none' && !AUTO_CLEAR_VALUES.includes(normalized)) {
      errors.push(`Invalid Auto-Clear Alarms value: "${value}". Must be one of: all, acked, none`);
    }

    return { valid: errors.length === 0, errors };
  }

  /**
   * Validate enabled alarm has required value
   */
  validateAlarmValue(alarmLevel: string, value: number | null | undefined, enabled: boolean): ValidationResult {
    const errors: string[] = [];

    if (enabled && (value === null || value === undefined)) {
      errors.push(`${alarmLevel} alarm is enabled but value is missing or invalid`);
    }

    return { valid: errors.length === 0, errors };
  }

}
